/** @format */

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import styled from 'styled-components'
import dayjs, { Dayjs } from 'dayjs'
import { _$, Button, ButtonGroup, Datepicker, Message } from '@king-design/react'
import type { Shortcut } from '@king-design/react/components/datepicker/shortcuts'
import IconButton from 'src/components/IconButton'
import {
  CUSTOM_RANGE_KEY,
  CustomDatePickerValue,
  DEFAULT_QUICK_RANGE,
  getQuickRangeDateValue,
  isEmptyCustomDatePickerValue,
  isRangeWithinMaxDays,
  msToSec,
  QuickRangeItem,
  resolveCustomDatePickerValue,
  secToMs
} from './constants'

export interface CustomDatePickerProps {
  className?: string
  /** 当前值：秒级时间戳或快捷选项 key（如 '1h'、'3day'）；为空则不选中 */
  value?: CustomDatePickerValue
  /** 默认快捷选项 key，如 '1h'；value 为空时生效；也为空则不选中 */
  defaultQuickKey?: string | null
  /** 时间范围变更回调，返回秒级时间戳 */
  onChange?: (value: [number, number], quickKey: string) => void
  /** 快捷选项配置，默认近1小时/近1天/近3天/近7天/自定义，可用 buildQuickRange 按需组合 */
  quickRange?: QuickRangeItem[]
  /** Datepicker 宽度 */
  datepickerWidth?: number | string
  /** 最大可选日期 */
  max?: Date | Dayjs
  /** 最小可选日期 */
  min?: Date | Dayjs
  /** 日期展示格式 */
  format?: string
  /** 开始与结束时间最大间隔（天），默认 30 */
  maxRange?: number
  /** 是否隐藏刷新按钮 */
  showRefresh?: boolean
  /** 快捷选项 */
  shortcuts?: Shortcut[]
}

/** ref 暴露的方法 */
export interface CustomDatePickerMethod {
  /** 按当前快捷项或自定义跨度，以当前时间为结束点重新计算时间范围 */
  reloadDate?: () => void
}

const CustomDatePicker = forwardRef<CustomDatePickerMethod, CustomDatePickerProps>(
  (
    {
      className,
      value,
      defaultQuickKey,
      onChange,
      quickRange = DEFAULT_QUICK_RANGE,
      datepickerWidth = 320,
      max = dayjs().endOf('day'),
      min,
      format = 'YYYY-MM-DD HH:mm',
      maxRange = 30,
      showRefresh = true,
      shortcuts
    },
    ref
  ) => {
    const resolveInput = useCallback(
      (input: CustomDatePickerValue) => resolveCustomDatePickerValue(input, quickRange),
      [quickRange]
    )

    const getInitialState = () => {
      const input = !isEmptyCustomDatePickerValue(value) ? value : defaultQuickKey
      const resolved = resolveCustomDatePickerValue(input, quickRange)
      return {
        selectedKey: resolved?.quickKey,
        dateValue: resolved?.range ? secToMs(resolved.range) : undefined
      }
    }

    const [selectedKey, setSelectedKey] = useState<string | undefined>(
      () => getInitialState().selectedKey
    )
    const [dateValue, setDateValue] = useState<[number, number] | undefined>(
      () => getInitialState().dateValue
    )
    const [pickerRevision, setPickerRevision] = useState(0)
    const lastValidRangeSecRef = useRef<[number, number] | undefined>(
      getInitialState().dateValue ? msToSec(getInitialState().dateValue) : undefined
    )
    const selectedKeyRef = useRef<string | undefined>(selectedKey)

    useEffect(() => {
      selectedKeyRef.current = selectedKey
    }, [selectedKey])

    const emitChange = useCallback(
      (range: [number, number], quickKey: string) => {
        if (!isRangeWithinMaxDays(range, maxRange)) {
          return
        }
        onChange?.(range, quickKey)
      },
      [onChange, maxRange]
    )

    const revertToLastValidRange = useCallback(() => {
      if (lastValidRangeSecRef.current) {
        setDateValue(secToMs(lastValidRangeSecRef.current))
      }
      setPickerRevision(rev => rev + 1)
    }, [])

    const commitRange = useCallback(
      (range: [number, number], quickKey: string, updateSelectedKey = true) => {
        if (!isRangeWithinMaxDays(range, maxRange)) {
          Message.error(_$(`时间范围不能超过{n}天`, { n: maxRange }))
          revertToLastValidRange()
          return false
        }
        if (updateSelectedKey) {
          setSelectedKey(quickKey)
        }
        lastValidRangeSecRef.current = range
        setDateValue(secToMs(range))
        emitChange(range, quickKey)
        return true
      },
      [maxRange, emitChange, revertToLastValidRange]
    )

    useEffect(() => {
      if (isEmptyCustomDatePickerValue(value)) {
        return
      }
      const resolved = resolveInput(value)
      if (!resolved) {
        return
      }
      setSelectedKey(resolved.quickKey)
      if (resolved.range) {
        lastValidRangeSecRef.current = resolved.range
        setDateValue(secToMs(resolved.range))
      }
    }, [value, resolveInput])

    const handleQuickChange = (key: string) => {
      if (key === CUSTOM_RANGE_KEY) {
        setSelectedKey(key)
        return
      }
      const range = getQuickRangeDateValue(key)
      if (range) {
        commitRange(range, key)
      }
    }

    const reloadDate = useCallback(() => {
      const key = selectedKeyRef.current
      if (!key) {
        return
      }
      if (key === CUSTOM_RANGE_KEY) {
        const last = lastValidRangeSecRef.current
        if (last) {
          const [start, end] = last
          const duration = end - start
          const newEnd = dayjs().unix()
          commitRange([newEnd - duration, newEnd], CUSTOM_RANGE_KEY, false)
        }
        return
      }
      const range = getQuickRangeDateValue(key)
      if (range) {
        commitRange(range, key, false)
      }
    }, [commitRange])

    useImperativeHandle(ref, () => ({ reloadDate }), [reloadDate])

    const handleDateChange = (
      newValue: [string | number, string | number] | null | undefined,
      _oldValue?: [string | number, string | number] | null | undefined
    ) => {
      if (!newValue || newValue.length < 2 || newValue[0] == null || newValue[1] == null) {
        return
      }
      const start = dayjs(newValue[0])
      const end = dayjs(newValue[1])
      if (!start.isValid() || !end.isValid()) {
        return
      }
      commitRange(msToSec([start.valueOf(), end.valueOf()]), CUSTOM_RANGE_KEY, false)
    }

    const showDatepicker = selectedKey === CUSTOM_RANGE_KEY

    return (
      <Container className={className}>
        <ButtonGroup checkType="radio" value={selectedKey} onChangeValue={handleQuickChange}>
          {quickRange.map(item => (
            <Button key={item.value} value={item.value}>
              {_$(item.label)}
            </Button>
          ))}
        </ButtonGroup>
        {showDatepicker && (
          <Datepicker
            shortcuts={shortcuts}
            key={pickerRevision}
            range
            type="datetime"
            clearable
            value={dateValue}
            format={format}
            width={datepickerWidth}
            max={max}
            min={min}
            onChangeValue={handleDateChange}
          />
        )}
        {showRefresh && (
          <IconButton type="refresh" title={_$('刷新')} hoverBackground onClick={reloadDate} />
        )}
      </Container>
    )
  }
)

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

CustomDatePicker.displayName = 'CustomDatePicker'

export default CustomDatePicker

export {
  ALL_QUICK_RANGE_ITEMS,
  buildQuickRange,
  CUSTOM_RANGE_KEY,
  DEFAULT_QUICK_RANGE,
  getQuickKeyFromRange,
  getQuickRangeDateValue,
  isEmptyCustomDatePickerValue,
  isKnownQuickRangeKey,
  isQuickRangeKey,
  isRangeWithinMaxDays,
  msToSec,
  QUICK_RANGE_PRESETS,
  resolveCustomDatePickerValue,
  secToMs
} from './constants'
export type { CustomDatePickerValue, QuickRangeItem } from './constants'
